import React from "react";
import {
  SimpleModal,
  CentralizedDrawer,
  useGlobalModalStack,
  useGlobalDrawerStack,
} from "@west-tennessee-consulting/ui";
import { EnhancedGenericCreateForm } from "../../../EnhancedGenericCreateForm";
import type { BaseEntity, SimpleTableConfig } from "../../../UniversalCRMTable/types";
import type { CreateFormOverrideProps, EntityFormProps } from "../../types";

type GlobalModalStack = ReturnType<typeof useGlobalModalStack>;

export const EntityCreateModals = <T extends BaseEntity>({
  entityName,
  config,
  globalModalStack,
  entityFormProps,
  handleItemCreated,
  onRefetch,
  CreateFormOverride,
}: {
  entityName: string;
  config: SimpleTableConfig<T>;
  globalModalStack: GlobalModalStack;
  entityFormProps?: EntityFormProps<T>;
  handleItemCreated: (data: Partial<T>) => void | Promise<void>;
  /** Refetch the list — passed to the override for endpoint-driven refreshes. */
  onRefetch?: () => void | Promise<void>;
  /** Optional richer create UI (e.g. social-post composer). Falls back to the generic form. */
  CreateFormOverride?: React.ComponentType<CreateFormOverrideProps<any>>;
}) => {
  const drawerStack = useGlobalDrawerStack();
  // The override persists through the same pipeline (onSubmit → create mutation),
  // so it renders in place of the generic form in both the modal and the drawer.
  const renderBody = (onClose: () => void) =>
    CreateFormOverride ? (
      <CreateFormOverride
        entityName={entityName}
        config={config}
        onSubmit={handleItemCreated}
        onClose={onClose}
        onRefetch={onRefetch}
      />
    ) : (
      <EnhancedGenericCreateForm<T>
        entityName={entityName}
        config={config}
        onSubmit={handleItemCreated}
        expandInlineVariant="floating"
        {...(entityFormProps || {})}
      />
    );

  return (
    <>
      {entityName !== "companies" && globalModalStack.isModalOpen(`create-${entityName}`) && (
        <SimpleModal
          opened={globalModalStack.isModalOpen(`create-${entityName}`)}
          onClose={() => globalModalStack.closeModal(`create-${entityName}`)}
          title={`Create New ${config.displayName}`}
          size="lg"
        >
          {renderBody(() => globalModalStack.closeModal(`create-${entityName}`))}
        </SimpleModal>
      )}

      <CentralizedDrawer
        drawerId={`create-${entityName}-drawer`}
        title={`Create New ${config.displayName}`}
        description={`Create a new ${config.displayName.toLowerCase()}`}
        side="bottom"
        height="95vh"
        enableSwipeGesture={true}
        showCloseButton={true}
      >
        {renderBody(() => drawerStack.closeDrawer(`create-${entityName}-drawer`))}
      </CentralizedDrawer>
    </>
  );
};
